import {useEffect, useRef} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {api} from '../api.js';

export type Tag = {id: string; name: string; color: string | null};
export type TaskPriority = 'critical' | 'high' | 'medium' | 'low';
export type TaskEnvironment = 'development' | 'testing' | 'integration' | 'early_access' | 'production';
export type Task = {id: string; title: string; notes: string; assigneeUserId: string | null; assigneeName: string | null; startOn: string | null; dueOn: string | null; completedAt: string | null; parentTaskId: string | null; sectionId: string | null; position: number; tags: Tag[]; priority: TaskPriority; environment: TaskEnvironment | null; releaseId: string | null; releaseVersion: string | null; milestoneId: string | null; milestoneName: string | null};
export type Section = {id: string; name: string; position: number; isBacklog: boolean; isDefault: boolean; workflowState: 'todo' | 'in_progress' | 'done'};
export type SectionTransition = {fromSectionId: string; toSectionId: string};
export type ReleaseSummary = {id: string; version: string; status: string};
export type CustomFieldType = 'text' | 'number' | 'single_select' | 'date' | 'people';
export type CustomFieldOption = {id: string; customFieldId: string; label: string; position: number};
export type CustomField = {id: string; name: string; fieldType: CustomFieldType; position: number; options: CustomFieldOption[]};
export type MyTask = {id: string; title: string; dueOn: string | null; completedAt: string | null; projectId: string; projectKey: string; projectName: string};

const sectionsKey = (projectId: string) => ['sections', projectId] as const;
const sectionTransitionsKey = (projectId: string) => ['section-transitions', projectId] as const;
const tasksKey = (projectId: string) => ['tasks', projectId] as const;
const tagsKey = (projectId: string) => ['tags', projectId] as const;
const customFieldsKey = (projectId: string) => ['custom-fields', projectId] as const;
const myTasksKey = ['my-tasks'] as const;

export function useMyTasks() {
  return useQuery({queryKey: myTasksKey, queryFn: () => api<{items: MyTask[]}>('/tasks/my').then(r => r.items)});
}

export function useToggleMyTaskComplete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (task: {id: string; completedAt: string | null}) => api(`/tasks/${task.id}/${task.completedAt ? 'reopen' : 'complete'}`, {method: 'POST'}),
    onSuccess: () => { void queryClient.invalidateQueries({queryKey: myTasksKey}); }
  });
}

export function useProjectSections(projectId: string) {
  return useQuery({queryKey: sectionsKey(projectId), queryFn: () => api<{items: Section[]}>(`/projects/${projectId}/sections`).then(r => r.items), enabled: Boolean(projectId)});
}

export function useProjectTasks(projectId: string) {
  return useQuery({queryKey: tasksKey(projectId), queryFn: () => api<{items: Task[]}>(`/projects/${projectId}/tasks`).then(r => r.items), enabled: Boolean(projectId)});
}

export function useCreateTask(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {title: string; sectionId: string | null}) => api<{id: string}>('/tasks', {method: 'POST', body: JSON.stringify({projectId, ...input})}),
    onSuccess: () => { void queryClient.invalidateQueries({queryKey: tasksKey(projectId)}); }
  });
}

export function useUpdateTaskFields(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {taskId: string; priority?: TaskPriority; environment?: TaskEnvironment | null; releaseId?: string | null; milestoneId?: string | null}) =>
      api(`/tasks/${input.taskId}/fields`, {method: 'PATCH', body: JSON.stringify({projectId, ...input, taskId: undefined})}),
    onMutate: async (input) => {
      await queryClient.cancelQueries({queryKey: tasksKey(projectId)});
      const previous = queryClient.getQueryData<Task[]>(tasksKey(projectId));
      queryClient.setQueryData<Task[]>(tasksKey(projectId), old => old?.map(candidate => candidate.id === input.taskId ? {...candidate, ...input} : candidate));
      return {previous};
    },
    onError: (_error, _input, context) => { if (context?.previous) queryClient.setQueryData(tasksKey(projectId), context.previous); },
    onSettled: () => { void queryClient.invalidateQueries({queryKey: tasksKey(projectId)}); }
  });
}

export function useProjectReleases(projectId: string) {
  return useQuery({queryKey: ['releases', projectId] as const, queryFn: () => api<{items: ReleaseSummary[]}>(`/projects/${projectId}/releases`).then(r => r.items), enabled: Boolean(projectId)});
}

export type ProjectDashboard = {
  project: {id: string; displayName: string; projectKey: string; currentReleaseId: string | null};
  taskCounts: {total: number; completed: number; overdue: number};
  blockers: Array<{id: string; taskId: string; title: string; priority: TaskPriority; assigneeName: string | null; createdAt: string}>;
  milestones: Array<{id: string; name: string; startDate: string | null; targetDate: string | null; status: string; total: number; completed: number}>;
  stages: Array<{environment: string; state: string; completedAt: string | null}>;
  readinessPct: number;
  activity: Array<{action: string; entityType: string; entityId: string; actorType: string; createdAt: string}>;
};

export function useProjectDashboard(projectKey: string) {
  return useQuery({queryKey: ['dashboard', projectKey] as const, queryFn: () => api<ProjectDashboard>(`/projects/${projectKey}/dashboard`), enabled: Boolean(projectKey)});
}

export type ReadinessCheck = {checkKey: string; state: 'pending' | 'passed' | 'failed'; updatedAt: string | null};

export function useReleaseReadiness(releaseId: string | null) {
  return useQuery({queryKey: ['readiness', releaseId] as const, queryFn: () => api<{items: ReadinessCheck[]}>(`/releases/${releaseId}/readiness`).then(r => r.items), enabled: Boolean(releaseId)});
}

export function useUpdateReadinessCheck(releaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {checkKey: string; state: 'pending' | 'passed' | 'failed'}) => api(`/releases/${releaseId}/readiness`, {method: 'PATCH', body: JSON.stringify(input)}),
    onSuccess: () => { void queryClient.invalidateQueries({queryKey: ['readiness', releaseId]}); }
  });
}

export function useRunValidation(releaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<{items: ReadinessCheck[]}>(`/releases/${releaseId}/readiness/validate`, {method: 'POST'}),
    onSuccess: () => { void queryClient.invalidateQueries({queryKey: ['readiness', releaseId]}); }
  });
}

export type Safeguards = {requireAutomatedTests: boolean; requireSecurityScan: boolean; requireManualApproval: boolean};

export function useSafeguards(projectId: string) {
  return useQuery({queryKey: ['safeguards', projectId] as const, queryFn: () => api<Safeguards>(`/projects/${projectId}/safeguards`), enabled: Boolean(projectId)});
}

export function useUpdateSafeguards(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<Safeguards>) => api<Safeguards>(`/projects/${projectId}/safeguards`, {method: 'PATCH', body: JSON.stringify(input)}),
    onSuccess: () => { void queryClient.invalidateQueries({queryKey: ['safeguards', projectId]}); }
  });
}

export type EnvironmentCheckConfig = {environment: TaskEnvironment; baseUrl: string | null; healthPath: string; enabled: boolean; intervalSeconds: number; timeoutMs: number; updatedAt: string | null; configured: boolean};
export type EnvironmentHealth = {environment: TaskEnvironment; status: 'healthy' | 'degraded' | 'down' | 'unconfigured'; uptimePct: number | null; incidents7d: number; lastCheckedAt: string | null; lastResponse: Record<string, unknown> | null};

export function useEnvironmentChecks(projectId: string) {
  return useQuery({queryKey: ['environment-checks', projectId] as const, queryFn: () => api<{items: EnvironmentCheckConfig[]}>(`/projects/${projectId}/environment-checks`).then(r => r.items), enabled: Boolean(projectId)});
}

export function useUpdateEnvironmentCheck(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {environment: TaskEnvironment; baseUrl: string; healthPath: string; enabled: boolean; intervalSeconds: number; timeoutMs: number}) => api(`/projects/${projectId}/environment-checks`, {method: 'PATCH', body: JSON.stringify(input)}),
    onSuccess: () => { void queryClient.invalidateQueries({queryKey: ['environment-checks', projectId]}); void queryClient.invalidateQueries({queryKey: ['environment-health', projectId]}); }
  });
}

export function useEnvironmentHealth(projectId: string) {
  return useQuery({queryKey: ['environment-health', projectId] as const, queryFn: () => api<{items: EnvironmentHealth[]}>(`/projects/${projectId}/environment-health`).then(r => r.items), enabled: Boolean(projectId), refetchInterval: 30000});
}

export function useDeleteTask(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => api(`/tasks/${taskId}`, {method: 'DELETE'}),
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: tasksKey(projectId)});
      void queryClient.invalidateQueries({queryKey: myTasksKey});
    }
  });
}

export function useToggleTaskComplete(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (task: Task) => api(`/tasks/${task.id}/${task.completedAt ? 'reopen' : 'complete'}`, {method: 'POST'}),
    onMutate: async (task) => {
      await queryClient.cancelQueries({queryKey: tasksKey(projectId)});
      const previous = queryClient.getQueryData<Task[]>(tasksKey(projectId));
      queryClient.setQueryData<Task[]>(tasksKey(projectId), old => old?.map(candidate => candidate.id === task.id ? {...candidate, completedAt: candidate.completedAt ? null : new Date().toISOString()} : candidate));
      return {previous};
    },
    onError: (_error, _task, context) => { if (context?.previous) queryClient.setQueryData(tasksKey(projectId), context.previous); },
    onSettled: () => { void queryClient.invalidateQueries({queryKey: tasksKey(projectId)}); }
  });
}

export function useMoveTask(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {taskId: string; sectionId: string | null; beforeTaskId?: string | null; afterTaskId?: string | null}) =>
      api(`/tasks/${input.taskId}/position`, {method: 'PATCH', body: JSON.stringify({projectId, sectionId: input.sectionId, beforeTaskId: input.beforeTaskId ?? null, afterTaskId: input.afterTaskId ?? null})}),
    onMutate: async (input) => {
      await queryClient.cancelQueries({queryKey: tasksKey(projectId)});
      const previous = queryClient.getQueryData<Task[]>(tasksKey(projectId));
      queryClient.setQueryData<Task[]>(tasksKey(projectId), old => old?.map(candidate => candidate.id === input.taskId ? {...candidate, sectionId: input.sectionId} : candidate));
      return {previous};
    },
    onError: (_error, _input, context) => { if (context?.previous) queryClient.setQueryData(tasksKey(projectId), context.previous); },
    onSettled: () => { void queryClient.invalidateQueries({queryKey: tasksKey(projectId)}); }
  });
}

export function useRescheduleTask(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {taskId: string; startOn: string | null; dueOn: string | null}) =>
      api(`/tasks/${input.taskId}`, {method: 'PATCH', body: JSON.stringify({startOn: input.startOn, dueOn: input.dueOn})}),
    onMutate: async (input) => {
      await queryClient.cancelQueries({queryKey: tasksKey(projectId)});
      const previous = queryClient.getQueryData<Task[]>(tasksKey(projectId));
      queryClient.setQueryData<Task[]>(tasksKey(projectId), old => old?.map(candidate => candidate.id === input.taskId ? {...candidate, startOn: input.startOn, dueOn: input.dueOn} : candidate));
      return {previous};
    },
    onError: (_error, _input, context) => { if (context?.previous) queryClient.setQueryData(tasksKey(projectId), context.previous); },
    onSettled: () => { void queryClient.invalidateQueries({queryKey: tasksKey(projectId)}); }
  });
}

export function useCreateSection(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {name: string; isBacklog?: boolean; isDefault?: boolean; workflowState?: Section['workflowState']}) => api<{id: string; name: string; position: number; isBacklog: boolean; isDefault: boolean}>(`/projects/${projectId}/sections`, {method: 'POST', body: JSON.stringify(input)}),
    onSuccess: () => { void queryClient.invalidateQueries({queryKey: sectionsKey(projectId)}); }
  });
}

export function useUpdateSectionWorkflowState(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {sectionId: string; workflowState: Section['workflowState']}) => api(`/sections/${input.sectionId}/workflow-state`, {method: 'PATCH', body: JSON.stringify({workflowState: input.workflowState})}),
    onSuccess: () => { void queryClient.invalidateQueries({queryKey: sectionsKey(projectId)}); }
  });
}

export function useEnsureDefaultSections(projectId: string, sections: Section[] | undefined) {
  const createSectionMutation = useCreateSection(projectId);
  const ensuredDefault = useRef(false);
  const ensuredBacklog = useRef(false);
  useEffect(() => {
    if (!sections) return;
    if (!sections.some(section => section.isDefault) && !ensuredDefault.current) {
      ensuredDefault.current = true;
      createSectionMutation.mutateAsync({name: 'Ready', isDefault: true}).catch(() => {});
    }
    if (!sections.some(section => section.isBacklog) && !ensuredBacklog.current) {
      ensuredBacklog.current = true;
      createSectionMutation.mutateAsync({name: 'Backlog', isBacklog: true}).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);
}

export function useRenameSection(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {sectionId: string; name: string}) => api(`/sections/${input.sectionId}`, {method: 'PATCH', body: JSON.stringify({name: input.name})}),
    onSuccess: () => { void queryClient.invalidateQueries({queryKey: sectionsKey(projectId)}); }
  });
}

export function useReorderSection(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {sectionId: string; beforeSectionId?: string | null; afterSectionId?: string | null}) => api(`/sections/${input.sectionId}/position`, {method: 'PATCH', body: JSON.stringify({beforeSectionId: input.beforeSectionId ?? null, afterSectionId: input.afterSectionId ?? null})}),
    onSettled: () => { void queryClient.invalidateQueries({queryKey: sectionsKey(projectId)}); }
  });
}

export function useDeleteSection(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sectionId: string) => api(`/sections/${sectionId}`, {method: 'DELETE'}),
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: sectionsKey(projectId)});
      void queryClient.invalidateQueries({queryKey: tasksKey(projectId)});
    }
  });
}

export function useProjectSectionTransitions(projectId: string) {
  return useQuery({queryKey: sectionTransitionsKey(projectId), queryFn: () => api<{items: SectionTransition[]}>(`/projects/${projectId}/section-transitions`).then(r => r.items), enabled: Boolean(projectId)});
}

export function useCreateSectionTransition(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SectionTransition) => api(`/projects/${projectId}/section-transitions`, {method: 'POST', body: JSON.stringify(input)}),
    onSuccess: () => { void queryClient.invalidateQueries({queryKey: sectionTransitionsKey(projectId)}); }
  });
}

export function useDeleteSectionTransition(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SectionTransition) => api(`/projects/${projectId}/section-transitions/${input.fromSectionId}/${input.toSectionId}`, {method: 'DELETE'}),
    onSuccess: () => { void queryClient.invalidateQueries({queryKey: sectionTransitionsKey(projectId)}); }
  });
}

export function useProjectTags(projectId: string) {
  return useQuery({queryKey: tagsKey(projectId), queryFn: () => api<{items: Tag[]}>(`/projects/${projectId}/tags`).then(r => r.items), enabled: Boolean(projectId)});
}

export function useCreateTag(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {name: string; color?: string | null}) => api<Tag>(`/projects/${projectId}/tags`, {method: 'POST', body: JSON.stringify(input)}),
    onSuccess: () => { void queryClient.invalidateQueries({queryKey: tagsKey(projectId)}); }
  });
}

export function useDeleteTag(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagId: string) => api(`/tags/${tagId}`, {method: 'DELETE'}),
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: tagsKey(projectId)});
      void queryClient.invalidateQueries({queryKey: tasksKey(projectId)});
    }
  });
}

export function useProjectCustomFields(projectId: string) {
  return useQuery({queryKey: customFieldsKey(projectId), queryFn: () => api<{items: CustomField[]}>(`/projects/${projectId}/custom-fields`).then(r => r.items), enabled: Boolean(projectId)});
}

export function useCreateCustomField(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {name: string; fieldType: CustomFieldType; options?: string[]}) => api<{id: string}>(`/projects/${projectId}/custom-fields`, {method: 'POST', body: JSON.stringify(input)}),
    onSuccess: () => { void queryClient.invalidateQueries({queryKey: customFieldsKey(projectId)}); }
  });
}

export function useDeleteCustomField(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fieldId: string) => api(`/custom-fields/${fieldId}`, {method: 'DELETE'}),
    onSuccess: () => { void queryClient.invalidateQueries({queryKey: customFieldsKey(projectId)}); }
  });
}
